import { useEffect, useState } from "react";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

function formatAge(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

export interface LastUpdatedIndicatorProps {
  updatedAt: number;
  isFetching: boolean;
  staleAfterMs: number;
  testId?: string;
  staleSuffix?: string;
}

export function LastUpdatedIndicator({
  updatedAt,
  isFetching,
  staleAfterMs,
  testId,
  staleSuffix = " — data may be out of date",
}: LastUpdatedIndicatorProps) {
  // Tick once per second so the relative label stays accurate between
  // refetches without re-running the query.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // `dataUpdatedAt` is 0 until the first successful fetch resolves. The parent
  // already gates on `isLoading`, so this is just a defensive guard.
  if (!updatedAt) return null;

  const ageMs = Math.max(0, now - updatedAt);
  const stale = ageMs > staleAfterMs;
  const fullTimestamp = new Date(updatedAt).toLocaleString();

  let icon;
  let text;
  if (isFetching) {
    icon = <Loader2 className="w-3 h-3 animate-spin" />;
    text = "Refreshing…";
  } else if (stale) {
    icon = <WifiOff className="w-3 h-3" />;
    text = `Last updated ${formatAge(ageMs)} ago${staleSuffix}`;
  } else if (ageMs < 5_000) {
    icon = <RefreshCw className="w-3 h-3" />;
    text = "Updated just now";
  } else {
    icon = <RefreshCw className="w-3 h-3" />;
    text = `Updated ${formatAge(ageMs)} ago`;
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs ${
        stale && !isFetching
          ? "text-amber-700 dark:text-amber-400"
          : "text-muted-foreground"
      }`}
      title={`Last refreshed ${fullTimestamp}`}
      data-testid={testId}
      data-stale={stale && !isFetching ? "true" : "false"}
      aria-live="polite"
    >
      {icon}
      <span>{text}</span>
    </div>
  );
}
